import { IPromise, IScope } from 'angular'
import { ApiProduct } from '@vend/types'
import {
  makeProduct,
  generateVariants,
  getExistingVariantCombinationDetails,
  InitialVariantAttributeConfig,
} from '@vend/business-components'
import { $q } from '../../../ng-compat/imports'
import { TaxManager } from '../../../managers/tax-manager'
import { RetailerSettingsManager } from '../../../managers/retailer-settings-manager'
import {
  convertVariantsToInitialQuickProduct,
  getExistingVariantAttributes,
  getRestorableGapVariantCombos,
  getTaxRateByGroup,
  pushNewValuesToExistingVariantAttribute,
} from '../utils'

interface VariantPickerModalOptions {
  title: string
  parentProduct: ApiProduct
  variantProducts: ApiProduct[]
  allowVariantParentSelect: boolean
  initialVariantAttributeConfig: InitialVariantAttributeConfig
}

/**
 * Controller class for the variant picker modal
 *
 * @class VariantPickerModalController
 */
export class VariantPickerModalController {
  public readonly close: () => void
  private readonly internalClose: (val: IPromise<any>) => void

  public onClose: (val: IPromise<any>) => void

  constructor(
    $scope: IScope,
    close: (val: IPromise<any>) => void,
    public readonly options: VariantPickerModalOptions,
    private readonly vdEscapeStack: any,
    private productModals: $TSFixMe,
    private retailerSettingsManager: RetailerSettingsManager,
    private taxManager: TaxManager,
    private variantPickerModalService: $TSFixMe
  ) {
    'ngInject'
    this.internalClose = close
    this.close = () => this.internalClose($q.reject())

    // Handle escape key
    this.vdEscapeStack.register(this.close)
    $scope.$on('$destroy', () => this.vdEscapeStack.deregister(this.close))
    this.onClose = this.onModalClose.bind(this)
  }

  public async onModalClose(val: IPromise<any>) {
    const result = await val
    if (result?.hasNewVariantAttribute) {
      const existingVariantAttributes = getExistingVariantAttributes(
        this.options.parentProduct,
        this.options.variantProducts
      )
      const newVariantAttributes = pushNewValuesToExistingVariantAttribute(
        existingVariantAttributes,
        result.newVariantAttribute
      )

      const isTaxExclusive = this.retailerSettingsManager.get().tax_exclusive
      const variantTaxRateMap: { [id: string]: number } = {}
      if (!isTaxExclusive) {
        // Get each variant's tax group if it is tax inclusive store
        const variantTaxGroups = await Promise.all(
          this.options.variantProducts.map(v =>
            this.taxManager.getTaxForProduct(v.id)
          )
        )
        this.options.variantProducts.forEach((v, index) => {
          const taxGroup = variantTaxGroups[index].tax
          variantTaxRateMap[v.id] = getTaxRateByGroup(taxGroup)
        })
      }

      const prod = convertVariantsToInitialQuickProduct(
        this.options.parentProduct,
        this.options.variantProducts,
        isTaxExclusive,
        variantTaxRateMap,
        newVariantAttributes
      )

      const restorableGapVariantCombinations = getRestorableGapVariantCombos(
        existingVariantAttributes,
        result.newVariantAttribute,
        result.selectedAttributeValues
      )

      const product = makeProduct(prod)

      const existingVariantComboDetails = getExistingVariantCombinationDetails(
        product,
        existingVariantAttributes,
        restorableGapVariantCombinations
      )
      const variants = await generateVariants(
        product,
        existingVariantComboDetails
      )

      const onBack = () =>
        this.variantPickerModalService.openVariantPicker({
          parentProduct: this.options.parentProduct,
          allowVariantParentSelect: this.options.allowVariantParentSelect,
          title: this.options.title,
          variantProducts: this.options.variantProducts,
          initialVariantAttributeConfig: {
            newVariantAttribute: result.newVariantAttribute,
            selectedAttributeValues: result.selectedAttributeValues,
          },
        })

      this.productModals.quickAddVariantsToExistingProduct(
        variants,
        existingVariantAttributes,
        existingVariantComboDetails,
        onBack
      )
    }

    this.internalClose(
      result?.hasNewVariantAttribute ? Promise.resolve(undefined) : val
    )
  }
}
